import { toast } from 'react-toastify';
import { ToastContent } from '@components/ToastContent';
import { syncData } from '@mutations/syncData';
import { PPLSyncStatus } from '@root/@types/types';
import { useMutation } from '@tanstack/react-query';
import { get } from 'lodash-es';

import { PPL_ERROR_MESSAGE, SYNC_DATA_MUTATION_KEY } from '../constants';
import { useSyncDataStore } from '../store';
import type { UseSyncDataProps } from '../types';
import { formatErrorMessage, getSuccessToastProps } from '../utils/toast';

export const useSyncData = ({ onCloseWhenSuccess, onOpen, reloadSubmission }: UseSyncDataProps) => {
  const { mutate, isPending, data } = useMutation({
    mutationKey: SYNC_DATA_MUTATION_KEY,
    mutationFn: syncData,
    async onSettled(data) {
      const errors = get(data, 'error.errors', null);
      const errorPayload = get(data, 'error.payload', null);
      const successPayload = get(data, 'payload', null);

      useSyncDataStore.getState().setErrors(errors);
      useSyncDataStore.getState().setPayload(errorPayload ?? successPayload);

      if (errors || !data) {
        const errorMessage = formatErrorMessage(errors ?? []);
        toast.error(<ToastContent title="Data Sync Failed" description={errorMessage ?? PPL_ERROR_MESSAGE} />, {
          autoClose: false,
        });
        return;
      }

      if (data.message === PPLSyncStatus.ValidationError) {
        toast.error(
          <ToastContent title="Data Sync Failed" description="Your data is not in sync, please try again" />,
          {
            autoClose: false,
          },
        );
        return;
      }

      if (data.message === PPLSyncStatus.NeedsDecision) {
        onOpen();
        return;
      }

      await reloadSubmission();

      const toastProps = getSuccessToastProps(data.message);

      if (!toastProps) {
        return onCloseWhenSuccess();
      }

      if (toastProps.kind === 'info') {
        toast.info(<ToastContent {...toastProps} />);
      } else {
        toast.success(<ToastContent {...toastProps} />);
      }

      onCloseWhenSuccess();
    },
  });

  return { onSync: mutate, isLoading: isPending, data };
};
